import { useEffect, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { ListingDraft } from "@/features/listings/types/listing";

const CONDITION_LABEL: Record<string, string> = {
  new: "New",
  "like-new": "Like New",
  used: "Used",
  "for-parts": "For Parts",
};

const TIPS = [
  "Use natural light — avoid flash and dark rooms.",
  "Show the item from multiple angles, including any flaws.",
  "A clean, uncluttered background helps buyers focus on the item.",
];

interface TipsSidebarCardProps {
  draft: ListingDraft;
}

/**
 * docs/ui/component-inventory.md's `TipsSidebarCard` + `LivePreviewSkeletonCard`:
 * desktop-only (steps 1–2), photography tips + a live-updating preview of
 * the in-progress listing. Each field renders as its own skeleton segment
 * until filled — "partially filled" is this component's normal resting
 * state, not something to resolve, per that doc's own note.
 */
export function TipsSidebarCard({ draft }: TipsSidebarCardProps) {
  const coverImage = draft.images[0];
  const coverUrl = useMemo(
    () => (coverImage ? URL.createObjectURL(coverImage) : null),
    [coverImage],
  );

  useEffect(() => {
    return () => {
      if (coverUrl) URL.revokeObjectURL(coverUrl);
    };
  }, [coverUrl]);

  return (
    <div className="hidden w-[280px] shrink-0 space-y-6 lg:block">
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-body-sm font-medium text-foreground">Photography tips</h3>
        <ul className="space-y-2">
          {TIPS.map((tip) => (
            <li key={tip} className="text-body-sm text-muted-foreground">
              • {tip}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-body-sm font-medium text-foreground">Live preview</h3>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="aspect-square w-full bg-muted">
            {coverUrl ? (
              <img src={coverUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <Skeleton className="h-full w-full rounded-none" />
            )}
          </div>
          <div className="space-y-1.5 p-3">
            {draft.price ? (
              <p className="text-body-lg font-semibold text-foreground">₹{draft.price}</p>
            ) : (
              <Skeleton className="h-5 w-16" />
            )}
            {draft.title ? (
              <p className="truncate text-body text-foreground">{draft.title}</p>
            ) : (
              <Skeleton className="h-4 w-full" />
            )}
            {draft.condition ? (
              <Badge variant="neutral">{CONDITION_LABEL[draft.condition]}</Badge>
            ) : (
              <Skeleton className="h-4 w-14" />
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-1 text-body-sm font-medium text-foreground">Need help?</h3>
        <p className="text-body-sm text-muted-foreground">
          Visit the Help Center for tips on pricing and safe meetups.
        </p>
      </div>
    </div>
  );
}
